import React from 'react';
import VideoContainer from 'src/images/video-containers/Rectangle2.svg';
import AutoplayVideo from 'src/components/AutoplayVideo';
import useResizeObserver from 'use-resize-observer';
import { createUseStyles } from 'react-jss';
import { spacing, breakpoint, pxToRem } from 'src/theme';
import { assetLink } from 'src/utils';
import Typography from 'src/components/core/Typography';
import SectionContainer from '../shared/SectionContainer';

const videoWidthToHeightRatio = 0.65;
const videoHeightOffset = 0.35;
const videoMaxWidth = pxToRem(900);

const verticalLargePadding = `calc(min(${videoMaxWidth}, 50vw) * ${
  videoWidthToHeightRatio
} * ${
  videoHeightOffset
})`;
const verticalSmallPadding = `calc(min(${videoMaxWidth}, 75vw) * ${
  videoWidthToHeightRatio
} * ${
  videoHeightOffset
})`;

export const useSectionAboveStyles = createUseStyles({
  root: {
    paddingBottom: verticalLargePadding,
    [breakpoint.down('md')]: {
      paddingBottom: verticalSmallPadding,
      paddingTop: 0,
    },
  },
});

const useStyles = createUseStyles({
  container: {
    overflow: 'visible',
    background: '#F7F7F7',
    // CSS trick so that we can have a video which is not absolute positioned
    // and still overlap with the above section
    paddingTop: '1px !important',
  },
  grid: {
    display: 'grid',
    justifyContent: 'center',
    columnGap: spacing(10),
    rowGap: spacing(10),
    marginTop: spacing(15),
    gridTemplateColumns: `repeat(3, minmax(0, ${pxToRem(420)}))`,
    [breakpoint.down('sm')]: {
      gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
      marginTop: spacing(10),
    },
  },
  video: {
    maxWidth: videoMaxWidth,
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '60vw',
    [breakpoint.down('md')]: {
      width: '80vw',
    },
  },
  icon: {
    height: pxToRem(60),
  },
});

const content: {
  iconSrc: string,
  label: string,
  details: string,
}[] = [
  {
    iconSrc: assetLink('landing-ui/icons/ProductivityIcon.svg'),
    label: 'Improve Productivity',
    details: 'Built for small businesses, with dedicated features to get work done faster.',
  },
  {
    iconSrc: assetLink('landing-ui/icons/EasyIcon.svg'),
    label: 'Easy To Use',
    details: 'Designed for simplicity and ease of use, with no training required.',
  },
  {
    iconSrc: assetLink('landing-ui/icons/LaunchIcon.svg'),
    label: 'Launch Instantly',
    details: 'Start with a new email system or migrate an existing one in minutes.',
  },
];

const EmailModuleDetails: React.FC = () => {
  const classes = useStyles();
  const {
    height: videoHeight = 0,
    ref: videoRef,
  } = useResizeObserver<HTMLDivElement>();
  return (
    <SectionContainer className={classes.container}>
      <div
        style={{
          position: 'relative',
          marginTop: -videoHeight * videoHeightOffset,
        }}
        className={classes.video}
        ref={videoRef}
      >
        <AutoplayVideo
          src={assetLink('landing-ui/videos/EmailModuleShowcase.mp4')}
          style={{
            position: 'absolute',
            width: '91%',
            top: '4%',
            left: '5%',
          }}
        />
        <VideoContainer style={{ width: '100%', height: '100%' }} />
      </div>
      <div className={classes.grid}>
        {content.map(({ iconSrc, label, details }) => (
          <div className="flex flex-col items-center" key={label}>
            <img src={iconSrc} alt={`${label} Icon`} className={classes.icon} />
            <Typography variant="h3" color="blue" centered style={{ marginTop: spacing(6) }}>
              {label}
            </Typography>
            <Typography variant="p1" color="blue" centered style={{ marginTop: spacing(2) }}>
              {details}
            </Typography>
          </div>
        ))}
      </div>
    </SectionContainer>
  );
};

export default EmailModuleDetails;
